import { useState, type FormEvent } from "react";
import { apiFetch } from "../api/client";
import type { SafeUser } from "../api/types";
import { useFetch } from "../hooks/useFetch";
import { ErrorBanner } from "../components/ErrorBanner";
import {
  EmptyState,
  LoadingState,
  PageHeader,
} from "../components/ui";

interface UsersResponse {
  users: SafeUser[];
}
interface CreateUserResponse {
  user: SafeUser;
  password?: string;
}
interface ResetPasswordResponse {
  user: SafeUser;
  password: string;
}
interface CsvImportResponse {
  created: Array<{ username: string; password: string }>;
}

export function UsersPage() {
  const { data, loading, error, refetch } = useFetch<UsersResponse>(
    "/admin/users",
  );
  const [generated, setGenerated] = useState<
    Array<{ username: string; password: string }>
  >([]);
  const [actionError, setActionError] = useState<Error | null>(null);

  function showError(err: unknown) {
    setActionError(err instanceof Error ? err : new Error(String(err)));
  }

  async function createUser(form: {
    username: string;
    display_name: string;
    class_name: string;
  }) {
    setActionError(null);
    try {
      const res = await apiFetch<CreateUserResponse>("/admin/users", {
        method: "POST",
        body: JSON.stringify({
          username: form.username,
          display_name: form.display_name || undefined,
          class_name: form.class_name || undefined,
        }),
      });
      if (res.password) {
        setGenerated((g) => [
          ...g,
          { username: res.user.username, password: res.password! },
        ]);
      }
      refetch();
    } catch (e) {
      showError(e);
    }
  }

  async function resetPassword(userId: string) {
    setActionError(null);
    try {
      const res = await apiFetch<ResetPasswordResponse>(
        `/admin/users/${userId}/reset-password`,
        { method: "POST", body: JSON.stringify({}) },
      );
      setGenerated((g) => [
        ...g,
        { username: res.user.username, password: res.password },
      ]);
    } catch (e) {
      showError(e);
    }
  }

  async function deactivate(userId: string) {
    setActionError(null);
    try {
      await apiFetch(`/admin/users/${userId}`, {
        method: "PUT",
        body: JSON.stringify({ is_active: false }),
      });
      refetch();
    } catch (e) {
      showError(e);
    }
  }

  async function importCsv(csv: string) {
    setActionError(null);
    try {
      const res = await apiFetch<CsvImportResponse>("/admin/users/import-csv", {
        method: "POST",
        body: JSON.stringify({ csv }),
      });
      setGenerated((g) => [...g, ...res.created]);
      refetch();
    } catch (e) {
      showError(e);
    }
  }

  return (
    <section>
      <PageHeader
        title="User management"
        subtitle="Create students, reset passwords, deactivate accounts, or bulk-import via CSV."
      />
      <ErrorBanner error={error ?? actionError} />

      {generated.length > 0 && (
        <div className="credentials-banner" role="status">
          <strong>Generated credentials (shown once — copy them now):</strong>
          <ul>
            {generated.map((c, i) => (
              <li key={i}>
                <code>{c.username}</code> / <code>{c.password}</code>
              </li>
            ))}
          </ul>
          <button onClick={() => setGenerated([])}>Dismiss</button>
        </div>
      )}

      <CreateUserForm onSubmit={createUser} />
      <CsvImportForm onSubmit={importCsv} />

      <h2>Users</h2>
      {loading ? (
        <LoadingState />
      ) : data && data.users.length > 0 ? (
        <table>
          <thead>
            <tr>
              <th>Username</th>
              <th>Role</th>
              <th>Display name</th>
              <th>Class</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => (
              <tr key={u.id}>
                <td>{u.username}</td>
                <td>{u.role}</td>
                <td>{u.display_name ?? "—"}</td>
                <td>{u.class_name ?? "—"}</td>
                <td>
                  <span
                    className={`badge ${u.is_active ? "badge-active" : "badge-failed"}`}
                  >
                    {u.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>
                  <button onClick={() => void resetPassword(u.id)}>
                    Reset password
                  </button>
                  {u.is_active && u.role !== "admin" && (
                    <button
                      className="btn-danger"
                      onClick={() => void deactivate(u.id)}
                    >
                      Deactivate
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <EmptyState
          title="No users yet"
          hint="Create a student with the form above or import a CSV roster."
        />
      )}
    </section>
  );
}

function CreateUserForm({
  onSubmit,
}: {
  onSubmit: (form: {
    username: string;
    display_name: string;
    class_name: string;
  }) => void;
}) {
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [className, setClassName] = useState("");

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!username.trim()) return;
    onSubmit({
      username: username.trim(),
      display_name: displayName.trim(),
      class_name: className.trim(),
    });
    setUsername("");
    setDisplayName("");
    setClassName("");
  }

  return (
    <form className="inline-form admin-form" onSubmit={submit}>
      <fieldset>
        <legend>Create student</legend>
        <div className="datetime-row">
          <label className="field-block">
            Username
            <input
              className="field-wide"
              placeholder="e.g. alice.smith"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </label>
          <label className="field-block">
            Display name
            <input
              className="field-wide"
              placeholder="Alice Smith"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </label>
          <label className="field-block">
            Class
            <input
              className="field-wide"
              placeholder="10A"
              value={className}
              onChange={(e) => setClassName(e.target.value)}
            />
          </label>
        </div>
        <button type="submit" className="btn-primary">
          Create student
        </button>
      </fieldset>
    </form>
  );
}

function CsvImportForm({ onSubmit }: { onSubmit: (csv: string) => void }) {
  const [csv, setCsv] = useState("");
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!csv.trim()) return;
    onSubmit(csv);
    setCsv("");
  }
  return (
    <form className="inline-form admin-form" onSubmit={submit}>
      <fieldset>
        <legend>Import CSV</legend>
        <label className="field-block">
          CSV (header row required)
          <textarea
            className="admin-textarea"
            placeholder={"username,display_name,class_name\nalice,Alice A,10A"}
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
          />
        </label>
        <button type="submit" className="btn-primary">
          Import
        </button>
      </fieldset>
    </form>
  );
}
